import { Box, CircularProgress, Typography } from '@material-ui/core'
import clsx from 'clsx'
import React from 'react'
import { useClashList } from '../hooks'
import { useClashListStyles } from '../styles'
import { ClashFilterStatus } from '../types'
import { ClashCard } from './ClashCard'

interface ClashListProps {
    projectId: string | null
    treePath?: string | null
    onClashResolved?: (clashId: string) => void
    skipQuery?: boolean
    fullWidth?: boolean
    className?: string
    filterStatus?: ClashFilterStatus
}

/**
 * List component displaying Best Practice clashes
 * Supports filtering by resolved/active status
 *
 * Related PRs: 177160, 177715
 */
export const ClashList = ({
    projectId,
    treePath,
    onClashResolved,
    skipQuery = false,
    className,
    filterStatus = 'active',
}: ClashListProps) => {
    const classes = useClashListStyles()

    const { clashes, loading, error, handleClashResolved } = useClashList({
        projectId,
        treePath,
        filterStatus,
        skipQuery,
    })

    const handleResolved = (clashId: string) => {
        handleClashResolved(clashId)
        onClashResolved?.(clashId)
    }

    const emptyStateMessage =
        filterStatus === 'active'
            ? {
                  title: 'No active clashes',
                  subtitle: 'All clashes have been resolved.',
              }
            : {
                  title: 'No resolved clashes',
                  subtitle: 'No clashes have been resolved yet.',
              }

    if (loading) {
        return (
            <Box className={clsx(classes.loadingContainer, className)}>
                <CircularProgress />
                <Typography variant="body2" className={classes.loadingText}>
                    &nbsp; Loading clashes...
                </Typography>
            </Box>
        )
    }

    if (error) {
        return (
            <Box className={clsx(classes.emptyState, className)}>
                <Typography variant="body1" color="error" gutterBottom>
                    Failed to load clashes
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {error.message ||
                        'An unexpected error occurred. Please try again.'}
                </Typography>
            </Box>
        )
    }

    if (clashes.length === 0) {
        return (
            <Box className={clsx(classes.emptyState, className)}>
                <Typography variant="body1" gutterBottom>
                    {emptyStateMessage.title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {emptyStateMessage.subtitle}
                </Typography>
            </Box>
        )
    }

    return (
        <Box className={className}>
            {clashes.map((clash) => (
                <ClashCard
                    key={clash.id}
                    clash={clash}
                    onResolved={handleResolved}
                />
            ))}
        </Box>
    )
}
